import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import apiService from '@/service/api-service';
import { store } from '@/service/store';
import {useSignals} from "@preact/signals-react/runtime";


const CustomerNumberStep: React.FC = () => {
  useSignals();

  const { t } = useTranslation();

  const customerRepo = store.customers;
  const customers = customerRepo.getCustomerList().value;
  const loading = customerRepo.isLoading.value;

  const [checking, setChecking] = useState(false);
  const [isValidInput, setIsValidInput] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedValue, setSelectedValue] = useState<CustomerOption | number | null>(() => {
    const storedNumber = store.installationAssistant.customerNumber.value;
    if (storedNumber) {
      const customer = customers.find(c => c.customerNumber === storedNumber);
      return customer ? `${storedNumber} - ${customer.businessName}` : storedNumber;
    }
    return null;
  });

  useEffect(() => {
    customerRepo.load();
  }, []);

  // Create customer options for the Autocomplete component
  const customerOptions = customerRepo.getCustomerList().value.map((elem) => {
    return {
      id: elem.customerNumber,
      label: `${elem.customerNumber} - ${elem.businessName}`,
    };
  });

  const checkCustomerExists = async (number: number) => {
    setChecking(true);
    setError(null);
    try {
      await apiService.head(`/customers/${number}/exists`);
      const numberValue = number;
      const isKnownCustomer = customers.some(c => parseInt(c.customerNumber) === numberValue);
      store.installationAssistant.customerNumber.value = number;
      store.installationAssistant.customerIsKnown.value = isKnownCustomer;
      setIsValidInput(true);

      // Find the matching customer option by id
      const matchingOption = customerOptions.find(option => option.id.toString() === number);
      setSelectedValue(matchingOption || number);
    } catch (error) {
      store.installationAssistant.customerNumber.value = "";
      store.installationAssistant.customerIsKnown.value = false;
      setIsValidInput(false);
      setError(t('assistant.customerNumberStep.customerNotFound'));
      setSelectedValue(null);
    } finally {
      setChecking(false);
    }
  };

  const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
    if (value === selectedValue+'') {
      return;
    }
    setSelectedValue(null);
    setError(null);
    setIsValidInput(false);
    store.installationAssistant.customerNumber.value = "";
    store.installationAssistant.customerIsKnown.value = false;

    const number = parseInt(value);

    if (customerRepo.get(number)) {
      const matchingOption = customerOptions.find(option => parseInt(option.id) === number);
      setSelectedValue(matchingOption || number);
      setIsValidInput(true);
      store.installationAssistant.customerNumber.value = number;
      store.installationAssistant.customerIsKnown.value = true;
      return;
    }

    if (!isNaN(number) && number >= 10000 && number <= 999999) {
      checkCustomerExists(number);
    }
  };

  interface CustomerOption {
    id: string;
    label: string;
  }

  const handleCustomerChange = async (_event: any, newValue: CustomerOption | null) => {
    if (!newValue) {
      setIsValidInput(false);
      setError(null);
      setSelectedValue(null);
      store.installationAssistant.customerNumber.value = "";
      store.installationAssistant.customerIsKnown.value = false;
      return;
    }

    const customerNum = newValue.id;
    const isKnownCustomer = customerRepo.getCustomerList().value.some(c => c.customerNumber === customerNum);

    if (isKnownCustomer) {
      setIsValidInput(true);
      setError(null);
      setSelectedValue(newValue);
      store.installationAssistant.customerNumber.value = customerNum;
      store.installationAssistant.customerIsKnown.value = true;
      return;
    }

    setSelectedValue(null);
    // Pass the customer number as a string to handleInputChange
    handleInputChange(null as any, customerNum);
  };



  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 'medium' }}>
        {t('assistant.customerNumberStep.title')}
      </Typography>

      <Typography variant="body2" sx={{ mb: 3 }}>
        {t('assistant.customerNumberStep.description')}
      </Typography>

      <Autocomplete
        freeSolo
        options={customerOptions}
        loading={loading}
        value={selectedValue}
        onChange={handleCustomerChange}
        onInputChange={handleInputChange}
        getOptionLabel={(option) => {
          // Handle both string and object options
          if (typeof option === 'string' || typeof option === 'number') {
            return option+'';
          }
          return option.label;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('assistant.customerNumberStep.customerNumber')}
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {(loading || checking) && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default CustomerNumberStep;
